import { __decorate, __extends } from "tslib";
import { serialize, serializeAsMeshReference } from "../Misc/decorators";
import { Tools } from "../Misc/tools";
import { TargetCamera } from "./targetCamera";
import { Matrix, Vector3 } from "../Maths/math.vector";
import { Node } from "../node";
import { FollowCameraInputsManager } from './followCameraInputsManager';
Node.AddNodeConstructor("FollowCamera", function (name, scene) {
    return function () { return new FollowCamera(name, Vector3.Zero(), scene); };
});
Node.AddNodeConstructor("ArcFollowCamera", function (name, scene) {
    return function () { return new ArcFollowCamera(name, 0, 0, 1.0, null, scene); };
});
/**
 * A follow camera takes a mesh as a target and follows it as it moves. Both a free camera version followCamera and
 * an arc rotate version arcFollowCamera are available.
 * @see https://doc.babylonjs.com/features/cameras#follow-camera
 */
var FollowCamera = /** @class */ (function (_super) {
    __extends(FollowCamera, _super);
    /**
     * Instantiates the follow camera.
     * @see https://doc.babylonjs.com/features/cameras#follow-camera
     * @param name Define the name of the camera in the scene
     * @param position Define the position of the camera
     * @param scene Define the scene the camera belong to
     * @param lockedTarget Define the target of the camera
     */
    function FollowCamera(name, position, scene, lockedTarget) {
        if (lockedTarget === void 0) { lockedTarget = null; }
        var _this = _super.call(this, name, position, scene) || this;
        /**
         * Distance the follow camera should follow an object at
         */
        _this.radius = 12;
        /**
         * Minimum allowed distance of the camera to the axis of rotation
         * (The camera can not get closer).
         * This can help limiting how the Camera is able to move in the scene.
         */
        _this.lowerRadiusLimit = null;
        /**
         * Maximum allowed distance of the camera to the axis of rotation
         * (The camera can not get further).
         * This can help limiting how the Camera is able to move in the scene.
         */
        _this.upperRadiusLimit = null;
        /**
         * Define a rotation offset between the camera and the object it follows
         */
        _this.rotationOffset = 0;
        /**
         * Minimum allowed angle to camera position relative to target object.
         * This can help limiting how the Camera is able to move in the scene.
         */
        _this.lowerRotationOffsetLimit = null;
        /**
         * Maximum allowed angle to camera position relative to target object.
         * This can help limiting how the Camera is able to move in the scene.
         */
        _this.upperRotationOffsetLimit = null;
        /**
         * Define a height offset between the camera and the object it follows.
         * It can help following an object from the top (like a car chaing a plane)
         */
        _this.heightOffset = 4;
        /**
         * Minimum allowed height of camera position relative to target object.
         * This can help limiting how the Camera is able to move in the scene.
         */
        _this.lowerHeightOffsetLimit = null;
        /**
         * Maximum allowed height of camera position relative to target object.
         * This can help limiting how the Camera is able to move in the scene.
         */
        _this.upperHeightOffsetLimit = null;
        /**
         * Define how fast the camera can accelerate to follow it s target.
         */
        _this.cameraAcceleration = 0.05;
        /**
         * Define the speed limit of the camera following an object.
         */
        _this.maxCameraSpeed = 20;
        _this.lockedTarget = lockedTarget;
        _this.inputs = new FollowCameraInputsManager(_this);
        _this.inputs.addKeyboard().addMouseWheel().addPointers();
        return _this;
        // Uncomment the following line when the relevant handlers have been implemented.
        // this.inputs.addKeyboard().addMouseWheel().addPointers().addVRDeviceOrientation();
    }
    FollowCamera.prototype._follow = function (cameraTarget) {
        if (!cameraTarget) {
            return;
        }
        var yRotation;
        if (cameraTarget.rotationQuaternion) {
            var rotMatrix = new Matrix();
            cameraTarget.rotationQuaternion.toRotationMatrix(rotMatrix);
            yRotation = Math.atan2(rotMatrix.m[8], rotMatrix.m[10]);
        }
        else {
            yRotation = cameraTarget.rotation.y;
        }
        var radians = Tools.ToRadians(this.rotationOffset) + yRotation;
        var targetPosition = cameraTarget.getAbsolutePosition();
        var targetX = targetPosition.x + Math.sin(radians) * this.radius;
        var targetZ = targetPosition.z + Math.cos(radians) * this.radius;
        var dx = targetX - this.position.x;
        var dy = (targetPosition.y + this.heightOffset) - this.position.y;
        var dz = (targetZ) - this.position.z;
        var vx = dx * this.cameraAcceleration * 2; //this is set to .05
        var vy = dy * this.cameraAcceleration;
        var vz = dz * this.cameraAcceleration * 2;
        if (vx > this.maxCameraSpeed || vx < -this.maxCameraSpeed) {
            vx = vx < 1 ? -this.maxCameraSpeed : this.maxCameraSpeed;
        }
        if (vy > this.maxCameraSpeed || vy < -this.maxCameraSpeed) {
            vy = vy < 1 ? -this.maxCameraSpeed : this.maxCameraSpeed;
        }
        if (vz > this.maxCameraSpeed || vz < -this.maxCameraSpeed) {
            vz = vz < 1 ? -this.maxCameraSpeed : this.maxCameraSpeed;
        }
        this.position = new Vector3(this.position.x + vx, this.position.y + vy, this.position.z + vz);
        this.setTarget(targetPosition);
    };
    /**
     * Attached controls to the current camera.
     * @param ignored defines an ignored parameter kept for backward compatibility. If you want to define the source input element, you can set engine.inputElement before calling camera.attachControl
     * @param noPreventDefault Defines whether event caught by the controls should call preventdefault() (https://developer.mozilla.org/en-US/docs/Web/API/Event/preventDefault)
     */
    FollowCamera.prototype.attachControl = function (ignored, noPreventDefault) {
        noPreventDefault = Tools.BackCompatCameraNoPreventDefault(arguments);
        this.inputs.attachElement(noPreventDefault);
        this._reset = function () {
        };
    };
    /**
     * Detach the current controls from the specified dom element.
     * @param ignored defines an ignored parameter kept for backward compatibility. If you want to define the source input element, you can set engine.inputElement before calling camera.attachControl
     */
    FollowCamera.prototype.detachControl = function (ignored) {
        this.inputs.detachElement();
        if (this._reset) {
            this._reset();
        }
    };
    /** @hidden */
    FollowCamera.prototype._checkInputs = function () {
        this.inputs.checkInputs();
        this._checkLimits();
        _super.prototype._checkInputs.call(this);
        if (this.lockedTarget) {
            this._follow(this.lockedTarget);
        }
    };
    FollowCamera.prototype._checkLimits = function () {
        if (this.lowerRadiusLimit !== null && this.radius < this.lowerRadiusLimit) {
            this.radius = this.lowerRadiusLimit;
        }
        if (this.upperRadiusLimit !== null && this.radius > this.upperRadiusLimit) {
            this.radius = this.upperRadiusLimit;
        }
        if (this.lowerHeightOffsetLimit !== null &&
            this.heightOffset < this.lowerHeightOffsetLimit) {
            this.heightOffset = this.lowerHeightOffsetLimit;
        }
        if (this.upperHeightOffsetLimit !== null &&
            this.heightOffset > this.upperHeightOffsetLimit) {
            this.heightOffset = this.upperHeightOffsetLimit;
        }
        if (this.lowerRotationOffsetLimit !== null &&
            this.rotationOffset < this.lowerRotationOffsetLimit) {
            this.rotationOffset = this.lowerRotationOffsetLimit;
        }
        if (this.upperRotationOffsetLimit !== null &&
            this.rotationOffset > this.upperRotationOffsetLimit) {
            this.rotationOffset = this.upperRotationOffsetLimit;
        }
    };
    /**
     * Gets the camera class name.
     * @returns the class name
     */
    FollowCamera.prototype.getClassName = function () {
        return "FollowCamera";
    };
    __decorate([
        serialize()
    ], FollowCamera.prototype, "radius", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "lowerRadiusLimit", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "upperRadiusLimit", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "rotationOffset", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "lowerRotationOffsetLimit", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "upperRotationOffsetLimit", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "heightOffset", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "lowerHeightOffsetLimit", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "upperHeightOffsetLimit", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "cameraAcceleration", void 0);
    __decorate([
        serialize()
    ], FollowCamera.prototype, "maxCameraSpeed", void 0);
    __decorate([
        serializeAsMeshReference("lockedTargetId")
    ], FollowCamera.prototype, "lockedTarget", void 0);
    return FollowCamera;
}(TargetCamera));
export { FollowCamera };
/**
 * Arc Rotate version of the follow camera.
 * It still follows a Defined mesh but in an Arc Rotate Camera fashion.
 * @see https://doc.babylonjs.com/features/cameras#follow-camera
 */
var ArcFollowCamera = /** @class */ (function (_super) {
    __extends(ArcFollowCamera, _super);
    /**
     * Instantiates a new ArcFollowCamera
     * @see https://doc.babylonjs.com/features/cameras#follow-camera
     * @param name Define the name of the camera
     * @param alpha Define the rotation angle of the camera around the logitudinal axis
     * @param beta Define the rotation angle of the camera around the elevation axis
     * @param radius Define the radius of the camera from its target point
     * @param target Define the target of the camera
     * @param scene Define the scene the camera belongs to
     */
    function ArcFollowCamera(name, 
    /** The longitudinal angle of the camera */
    alpha, 
    /** The latitudinal angle of the camera */
    beta, 
    /** The radius of the camera from its target */
    radius, 
    /** Define the camera target (the mesh it should follow) */
    target, scene) {
        var _this = _super.call(this, name, Vector3.Zero(), scene) || this;
        _this.alpha = alpha;
        _this.beta = beta;
        _this.radius = radius;
        _this._cartesianCoordinates = Vector3.Zero();
        _this._meshTarget = target;
        _this._follow();
        return _this;
    }
    ArcFollowCamera.prototype._follow = function () {
        if (!this._meshTarget) {
            return;
        }
        this._cartesianCoordinates.x = this.radius * Math.cos(this.alpha) * Math.cos(this.beta);
        this._cartesianCoordinates.y = this.radius * Math.sin(this.beta);
        this._cartesianCoordinates.z = this.radius * Math.sin(this.alpha) * Math.cos(this.beta);
        var targetPosition = this._meshTarget.getAbsolutePosition();
        this.position = targetPosition.add(this._cartesianCoordinates);
        this.setTarget(targetPosition);
    };
    /** @hidden */
    ArcFollowCamera.prototype._checkInputs = function () {
        _super.prototype._checkInputs.call(this);
        this._follow();
    };
    /**
     * Returns the class name of the object.
     * It is mostly used internally for serialization purposes.
     */
    ArcFollowCamera.prototype.getClassName = function () {
        return "ArcFollowCamera";
    };
    return ArcFollowCamera;
}(TargetCamera));
export { ArcFollowCamera };
//# sourceMappingURL=followCamera.js.map